<template>
  <div class="calendar-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'MeetingsPage_phone.png' : 'MeetingsPage.jpeg'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">My Calendar</h1>
    </div>

    <div class="page-wrapper">
      <div class="controls-row">
        <div class="tab-group">
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'upcoming' }" @click="activeTab = 'upcoming'">Upcoming</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'past' }" @click="activeTab = 'past'">Past</button>
        </div>

        <button v-if="activeTab === 'past' && filteredMeetings.length > 0" class="clear-btn" @click="clearPastMeetings">
          🗑 Clear
        </button>
      </div>

      <div v-if="filteredMeetings.length === 0" class="empty-state">
        <div class="empty-icon">📅</div>
        <p class="empty-text">No meetings here</p>
        <p class="empty-sub">{{
          activeTab === 'upcoming' ? 'Your upcoming meetings will appear here' :
          activeTab === 'past' ? 'No past meetings found' :
          activeTab === 'accepted' ? 'No accepted meetings yet' :
          'No denied meetings yet'
        }}</p>
      </div>

      <div v-else class="booking-grid">
        <div v-for="mt in filteredMeetings" :key="mt.id" class="booking-card">
          <div class="booking-date-block">
            <span class="booking-day">{{ formatDay(mt.date) }}</span>
            <span class="booking-month">{{ formatMonth(mt.date) }}</span>
          </div>
          <div class="booking-middle">
            <p class="booking-pet-name">{{ mt.pet_name }}</p>
            <p class="booking-time">{{ formatTime(mt.time) }}</p>
          </div>
          <span class="status-badge" :class="statusClass(mt.confirmation)">
            {{ getStatusLabel(mt.confirmation) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'CalendarPage',
  props: {
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      meetings: [],
      userType: null,
      syncing: false,
      previousMeetingCount: 0,
      sidebarOpen: false,
      activeTab: 'upcoming',
      petsOpen: false,
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
    };
  },
  computed: {
    filteredMeetings() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.meetings.filter(mt => {
        const mtDate = new Date(mt.date.toString().slice(0, 10) + 'T00:00:00');
        if (this.activeTab === 'upcoming') return mtDate >= today;
        if (this.activeTab === 'past') return mtDate < today;
        if (this.activeTab === 'accepted') return mt.confirmation === 1;
        if (this.activeTab === 'denied') return mt.confirmation === 0;
        return true;
      });
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    try {
      const { data: me } = await axios.get(`${backendUrl}/api/auth/me`, { withCredentials: true });
      this.userType = me.user_type;
    } catch (e) {
      console.error('Couldn\'t fetch user type', e);
      return;
    }
    await this.load();
    const hasSynced = localStorage.getItem('hasSyncedCalendar');
    if (!hasSynced && this.meetings.length > 0) {
      await this.syncToGoogle();
      localStorage.setItem('hasSyncedCalendar', 'true');
    }
    setInterval(this.load, 30000);
  },
  methods: {
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    getStatusLabel(status) {
      if (status === 1) return 'Accepted';
      if (status === 0) return 'Pending';
      return 'Completed';
    },
    async load() {
      try {
        const [{ data: booked }, { data: requests }] = await Promise.all([
          axios.get(`${backendUrl}/api/meetings/user`, { withCredentials: true }),
          axios.get(`${backendUrl}/api/meetings/requests`, { withCredentials: true }),
        ]);
        const merged = [...booked, ...requests];
        this.meetings = merged.filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i);
        this.previousMeetingCount = this.meetings.length;
      } catch (e) {
        console.error('Failed to load meetings', e);
      }
    },
    formatTime(timeStr) {
      let [h, m] = timeStr.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
    },
    formatDay(dateStr) {
      return String(new Date(dateStr).getDate()).padStart(2, '0');
    },
    formatMonth(dateStr) {
      return new Date(dateStr).toLocaleString('default', { month: 'short' }).toUpperCase();
    },
    statusClass(status) {
      if (status === 1) return 'status-accepted';
      if (status === 0) return 'status-pending';
      return 'status-completed';
    },
    async syncToGoogle() {
      this.syncing = true;
      try {
        const { data: pets } = await axios.get(`${backendUrl}/api/pets/pets`, { withCredentials: true });
        const events = this.meetings.map(m => {
          const pet = pets.find(p => p.pet_name === m.pet_name);
          if (!pet) return null;
          return { pet_id: pet.id, date: m.date, time: m.time };
        }).filter(e => e !== null);
        await axios.post(`${backendUrl}/api/meetings/sync-calendar`, { calendarEvents: events }, { withCredentials: true });
      } catch (err) {
        console.error('Sync failed:', err);
        const msg = err.response?.data?.error || '';
        if (msg.includes('No Google tokens')) {
          window.location.href = `${backendUrl}/api/auth/google`;
        }
      } finally {
        this.syncing = false;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
    async clearPastMeetings() {
      try {
        await axios.delete(`${backendUrl}/api/meetings/clear-past`, { withCredentials: true });
        // remove from local state too so UI updates instantly
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.meetings = this.meetings.filter(mt => {
          const mtDate = new Date(mt.date.toString().slice(0, 10) + 'T00:00:00');
          return mtDate >= today;
        });
      } catch (e) {
        console.error('Failed to clear past meetings:', e);
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
* { margin: 0; padding: 0; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

.calendar-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f5ece0;
  background-attachment: fixed;  /* ← add this */
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-title { font-size: 22px; font-weight: 800; color: #1a1a1a; letter-spacing: -0.4px; margin: 0; margin-top: 8px; margin-left: 7px;}
.page-sub { font-size: 13px; color: #aaa; font-weight: 500; margin-top: 2px; }

.page-wrapper { max-width: 1000px; margin: 0 auto; padding: 16px 20px 40px; }

.controls-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; gap: 12px; }

.tab-group { display: flex; background: #fff; border: 1px solid #f0e0ff; border-radius: 999px; padding: 4px; gap: 4px; }

.tab-btn { padding: 8px 20px; border-radius: 999px; border: none; background: none; font-size: 13px; font-weight: 700; color: #aaa; cursor: pointer; transition: all 0.18s ease; }
.tab-btn:hover { color: #4a1070; }
.tab-active { background: #eac7ff; color: #4a1070; }

.sync-btn { display: flex; align-items: center; gap: 6px; padding: 9px 20px; background: #ffec90; border: 1px solid #f6d860; border-radius: 999px; color: #92610a; font-size: 13px; font-weight: 700; cursor: pointer; transition: background 0.18s; white-space: nowrap; }
.sync-btn:hover { background: #ffe342; }
.sync-btn:disabled { opacity: 0.5; cursor: not-allowed; }

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.spinning { animation: spin 0.8s linear infinite; }

.booking-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; overflow-y: auto; max-height: calc(100vh - 250px); }

.booking-card { background: #fff; border-radius: 20px; border: 1px solid #f0e0ff; padding: 18px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 4px 16px rgba(186,113,255,0.07); transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s; }
.booking-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(186,113,255,0.13); }

.booking-date-block { flex-shrink: 0; width: 52px; height: 58px; background: linear-gradient(135deg, #eac7ff, #dda6fd); border-radius: 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; }
.booking-day { font-size: 20px; font-weight: 800; color: #4a1070; line-height: 1; }
.booking-month { font-size: 10px; font-weight: 700; color: #7a30a8; letter-spacing: 0.06em; }

.booking-middle { flex: 1; min-width: 0; }
.booking-pet-name { font-size: 15px; font-weight: 800; color: #1a1a1a; margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.booking-time { font-size: 13px; color: #999; font-weight: 600; margin: 0; }

.status-badge { flex-shrink: 0; font-size: 12px; font-weight: 700; padding: 5px 13px; border-radius: 999px; letter-spacing: 0.02em; }
.status-accepted { background: #d4f5e2; color: #1a6b3c; }
.status-completed { background: #d4f5e2; color: #1a6b3c; }
.status-pending { background: #fffbe8; color: #92610a; border: 1px solid #f6d860; }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 10px; }
.empty-icon { font-size: 48px; opacity: 0.5; }
.empty-text { font-size: 17px; font-weight: 700; color: #333; margin: 0; }
.empty-sub { font-size: 13px; color: #bbb; margin: 0; text-align: center; }

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

.clear-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: #fde8e8;
  border: 1px solid #f8b4b4;
  border-radius: 999px;
  color: #991b1b;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s;
  white-space: nowrap;
}

.clear-btn:hover { background: #f8b4b4; }


/* ── Mobile ── */
@media (max-width: 480px) {
  .page-header { padding: 20px 16px 0 46px; }
  .page-sub { font-size: 11px; }
  .page-wrapper { padding: 12px 14px 30px; }

  .controls-row { flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
  .tab-group { flex-wrap: wrap; border-radius: 14px; }
  .tab-btn { padding: 6px 10px; font-size: 12px; }
  .sync-btn { padding: 7px 14px; font-size: 12px; }

  .booking-grid { grid-template-columns: 1fr; gap: 10px; }

  .booking-card {
    padding: 10px;
    border-radius: 16px;
    gap: 10px;
  }

  .booking-date-block {
    width: 44px;
    height: 50px;
    border-radius: 10px;
  }

  .booking-day { font-size: 16px; }
  .booking-month { font-size: 9px; }
  .booking-pet-name { font-size: 13px; }
  .booking-time { font-size: 11px; }

  .status-badge {
    font-size: 10px;
    padding: 3px 8px;
  }
  .clear-btn { padding: 7px 14px; font-size: 12px; }
}
</style>